import pick from "lodash/pick";
import zipObject from "lodash/zipObject";
import {
  ActivityDataEntry,
  BookingDataEntry
} from "PFApp/booking/parts/overview/calendar/managers/booking_data_manager";
import { calendarRootSelector } from "PFApp/booking/store/booking_module_store_selectors";
import { Row } from "PFApp/booking/types";
import {
  CalendarMode,
  CalendarModeInitialData,
  Tabs,
  WorkforceMetrics,
  WorkforceMetricsMode
} from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { InitialTabData } from "PFApp/booking/types/initial_tab_data";
import { isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { CalendarRange, Threshold } from "PFTypes";
import { FiltersMetaV2 } from "PFTypes/filters_v2";
import { createContext, useContext } from "react";
import { StoreApi, useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import {
  CalendarRootStoreSlice,
  CalendarRootStoreSliceActions,
  VisibleRowsRange
} from "./calendar_root_store_slice.types";
import {
  ProjectsActions,
  ProjectsCounters,
  ProjectsEngagementsActions,
  ProjectsEngagementsSlice,
  ProjectsSlice
} from "./projects_store_slice.types";
import { WorkforceActions, WorkforceCounters, WorkforceSlice } from "./workforce_store_slice.types";
export type { ActivityDataEntry, BookingDataEntry };

export type BookingModuleStoreType = {
  tab: Tabs;
  calendarMode: CalendarMode;
  calendarRootByMode: Record<CalendarMode, CalendarRoot>;
  calendarRange: CalendarRange;
  headerDisabled: boolean;
  workforceMetrics: {
    mode: WorkforceMetricsMode;
    metrics: WorkforceMetrics | undefined;
    loading: boolean;
  };
  tooltipVirtualBoundaryElement: HTMLDivElement | null;
  thresholds: Record<string, Threshold[]>;
  loading: boolean;
  isMisalignmentsVisible: boolean;
  visibleRowsRange: VisibleRowsRange | null;
  rowHeightsByKeyCache: Map<string, number>;
  bookingsData: Record<number, BookingDataEntry>;
  auditActivitiesData: Record<number, ActivityDataEntry>;
  isBMManagersEnabled: boolean;
  [CalendarRoot.WorkforceProfiles]: CalendarRootStoreSlice<WorkforceSlice, WorkforceCounters>;
  [CalendarRoot.ProjectsEngagements]: CalendarRootStoreSlice<ProjectsEngagementsSlice, ProjectsCounters>;
  [CalendarRoot.ProjectsRoles]: CalendarRootStoreSlice<ProjectsSlice, ProjectsCounters>;
  actions: {
    setTab: (tab: Tabs) => void;
    changeTab: (tab: Tabs, initialTabData?: InitialTabData) => void;
    setCalendarMode: (calendarMode: CalendarMode, calendarModeInitialData?: CalendarModeInitialData) => void;
    setCalendarRootByMode: (
      calendarMode: CalendarMode,
      calendarRoot: CalendarRoot,
      filtersMetaCallback?: ((filtersMeta: FiltersMetaV2) => void) | null
    ) => void;
    setCalendarRange: (calendarRange: CalendarRange) => void;
    setHeaderDisabled: (headerDisabled: boolean) => void;
    setWorkforceMetrics: (
      workforceMetrics:
        | { mode: WorkforceMetricsMode }
        | {
            metrics: WorkforceMetrics | undefined;
            loading: boolean;
          }
    ) => void;
    setTooltipVirtualBoundaryElement: (node: HTMLDivElement | null) => void;
    setThresholds: (thresholds: Record<string, Threshold[]>) => void;
    setLoading: (loading: boolean) => void;
    setIsMisalignmentsVisible: (isMisalignmentsVisible: boolean) => void;
    setVisibleRowsRange: (range: VisibleRowsRange) => void;
    setRowHeightCache: (rowKey: string, height: number) => void;
    updateBookingsData: (bookingId: number, entry: Partial<BookingDataEntry>) => void;
    updateAuditActivitiesData: (activityId: number, entry: Partial<ActivityDataEntry>) => void;
    getCurrentRowsByKey: () => Record<string, Row>;
    [CalendarRoot.WorkforceProfiles]: CalendarRootStoreSliceActions<WorkforceCounters, WorkforceActions>;
    [CalendarRoot.ProjectsEngagements]: CalendarRootStoreSliceActions<
      ProjectsCounters,
      ProjectsEngagementsActions
    >;
    [CalendarRoot.ProjectsRoles]: CalendarRootStoreSliceActions<ProjectsCounters, ProjectsActions>;
  };
};

export const BookingModuleStoreContext = createContext<StoreApi<BookingModuleStoreType> | null>(null);

export const useBookingModuleStore = <U>(selector: (state: BookingModuleStoreType) => U) => {
  const store = useContext(BookingModuleStoreContext);
  if (!store) {
    throw new Error("Using BookingModuleStoreContext must be within a BookingModuleStoreContext.Provider");
  }
  return useStore<StoreApi<BookingModuleStoreType>, U>(store, useShallow(selector));
};

export const useBookingModuleStoreActions = () => {
  const store = useContext(BookingModuleStoreContext);
  if (!store) {
    throw new Error("Using BookingModuleStoreContext must be within a BookingModuleStoreContext.Provider");
  }
  return useStore(store, (state) => state.actions);
};

export const useCalendarRoot = () => useBookingModuleStore(calendarRootSelector);

export const useCalendarRootActions = (calendarRoot: CalendarRoot) => {
  const store = useContext(BookingModuleStoreContext);
  if (!store) {
    throw new Error("Using BookingModuleStoreContext must be within a BookingModuleStoreContext.Provider");
  }
  return useStore(store, (state) => state.actions[calendarRoot]);
};

export const useWorkforceProfilesDataIfAvailable = <K extends keyof WorkforceSlice>(...keys: K[]) => {
  const calendarRoot = useCalendarRoot();
  return useBookingModuleStore<Pick<WorkforceSlice, K> | Record<K, null>>((state) =>
    isWorkforceProfiles(calendarRoot)
      ? pick(state[CalendarRoot.WorkforceProfiles], keys)
      : (zipObject(
          keys,
          keys.map(() => null)
        ) as Record<K, null>)
  );
};
